import React, { useState } from 'react';

import { Button, Col, LoadingIndicator, MessageBlock, MessageBlockContent, ModalBody, ModalFooter, ModalHeader, Row, } from '@cfa/react-components';
import { IDevice, IDeviceQuery } from 'interfaces';
import { DevicesService, ManufacturersService, parseAxiosError } from 'services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IDeviceSearchModalProps {
    onSubmit: (thingIDs: string[]) => void,
    onHide: () => void,
}

export function DeviceSearchModal({ onSubmit, onHide }: IDeviceSearchModalProps) {

    // state
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    

    // device search list
    const [devices, setDevices] = useState<IDevice[]>([]);
    const [selectedDevices, setSelectedDevices] = useState<IDevice[]>([]);
    
    // select
    const [allSelected, setAllSelected] = useState(false);

    // form search values
    const [location, setLocation] = useState('');
    const [manufacturer, setManufacturer] = useState('');
    const [modelNumber, setModelNumber] = useState('');
    const [serialNumber, setSerialNumber] = useState('');

    const onKeypress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSubmitSearchValues();
        }
    }

    // form submission
    const onSubmitSearchValues = () => {
    
        if (manufacturer == "" && modelNumber == "" && location == "" && serialNumber == "") return;

        setLoading(true);
        setSelectedDevices([]);
        setDevices([]);
        setAllSelected(false);

        let query: IDeviceQuery = {
            thingID: "",
            manufacturerName: manufacturer,
            modelNumber: modelNumber,
            locationNumber: location,
            serialNumber: serialNumber,
            offset: 0,
            limit: 10, 
            sort: "lastSeenDesc",
        }
    
        DevicesService.searchDevices(query)
            .then((devs) => {
                doSuccess(devs);
            }).catch((err) => {
                // handle 404 composite query
                if (err.response.status == 404) {
                    doSuccess([]);
                    return;
                }
                setLoading(false);
                setErrorMessage(parseAxiosError(err))
            });
    };

    const doSuccess = (devs) => {
        setDevices(devs);
        setErrorMessage("");
        setLoading(false);
    };

    // thing id submission
    const onSubmitThingIDs = () => {
        setSubmitting(true);
        onSubmit(selectedDevices.map(d => d.thingID));
    }

    // select
    const isSelected = input => selectedDevices.some(({id}) => id == input);
    
    const onToggleSelectAll = () => {
        let newSelectedDevices;
        
        if (allSelected) newSelectedDevices = [];
        else newSelectedDevices = devices.slice();

        setAllSelected(!allSelected);
        setSelectedDevices(newSelectedDevices);
    }

    const onSelectDevice = (d: IDevice) => {
        let newSelectedDevices = selectedDevices.slice();

        if (isSelected(d.id)) {
            const deviceIndex = newSelectedDevices.findIndex(({id}) => id == d.id);
            newSelectedDevices.splice(deviceIndex, 1);
        }
        else newSelectedDevices.push(d);

        if (newSelectedDevices.length == devices.length) setAllSelected(true);
        else setAllSelected(false)

        setSelectedDevices(newSelectedDevices);
    }

    return (<>

        <ModalHeader>
            Search Devices
        </ModalHeader>

        <ModalBody>
            {errorMessage && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='error'>
                    <MessageBlockContent>{errorMessage.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            {loading && (<Row style={{justifyContent: 'center'}}>
                <LoadingIndicator size='md' variant='inline'>
                </LoadingIndicator>
            </Row>)}
            <Row>
                <Col className="searchPopover" lg={4} style={{padding: "0px 15px"}}>
                    <div className="searchPopover_group">
                        <div className="searchPopover_label">
                            {'Manufacturer'}
                        </div>
                        <div className="searchPopover_value">
                            <div className="inputWithButton">
                                <select value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}>
                                    {ManufacturersService.manufacturerNames.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                                <button type="button" onClick={onSubmitSearchValues}>
                                    <FontAwesomeIcon icon="angle-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="searchPopover_group">
                        <div className="searchPopover_label">
                            {'Model Number'}
                        </div>
                        <div className="searchPopover_value">
                            <div className="inputWithButton">
                                <input
                                    type="text"
                                    value={modelNumber}
                                    onChange={(e) => setModelNumber(e.target.value)}
                                    onKeyPress={onKeypress}
                                    maxLength={128}
                                />
                                <button type="button" onClick={onSubmitSearchValues}>
                                    <FontAwesomeIcon icon="angle-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="searchPopover_group">
                        <div className="searchPopover_label">
                            {'Location'}
                        </div>
                        <div className="searchPopover_value">
                            <div className="inputWithButton">
                                <input
                                    type="text"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    onKeyPress={onKeypress}
                                    maxLength={128}
                                />
                                <button type="button" onClick={onSubmitSearchValues}>
                                    <FontAwesomeIcon icon="angle-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="searchPopover_group">
                        <div className="searchPopover_label">
                            {'Serial Number'}
                        </div>
                        <div className="searchPopover_value">
                            <div className="inputWithButton">
                                <input
                                    type="text"
                                    value={serialNumber}
                                    onChange={(e) => setSerialNumber(e.target.value)}
                                    onKeyPress={onKeypress}
                                    maxLength={128}
                                />
                                <button type="button" onClick={onSubmitSearchValues}>
                                    <FontAwesomeIcon icon="angle-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col>
                    {devices.length > 0 && devices.map((d) => (
                        <li className={"devicesItem"} key={d.id}>
                            <div className="devicesItem_checkCol devicesItem_col">
                                <div className="devicesItem_checkCircle">
                                    <input 
                                        name={d.id + ''}
                                        type="checkbox"
                                        className="devicesItem_check" 
                                        onChange={() => onSelectDevice(d)}
                                        checked={isSelected(d.id)}
                                    />
                                </div>
                            </div>
                            <div className="deviceSearchItem_modalLink">
                                <div className="devicesItem_manufacturer ellipsis" title={d.thingID}>
                                    {d.thingID}
                                </div>
                            </div>
                        </li>
                    ))}
                    {devices.length === 0 &&
                        <div className="devicesList_empty">
                            {'No devices found.'}
                        </div>
                    }
                </Col>
            </Row>
        </ModalBody>

        <ModalFooter>
            <Button variant="outlined" onClick={onHide}>
                Close
            </Button>
            <Button variant="filled" type="submit" loading={submitting} onClick={onSubmitThingIDs} disabled={selectedDevices.length === 0}>
                {submitting ? 'Subscribing...' : 'Subscribe'}
            </Button>
        </ModalFooter>

    </>);
}
